/* MolCellQuest — 旅のエンジン（純関数のみ・DOM に触れない）
   進化ポイント(evo) → 時代 / 進捗 / 地質年代 / 節目 の変換を一手に引き受ける。 */

import { ERAS, FUTURE_LAP, FUTURE_LAP_MA } from "./eras.js";

/**
 * evo から現在地を求める。
 * @returns {{
 *   era: object, index: number, startEvo: number, endEvo: number,
 *   intoEra: number, fraction: number,
 *   isFuture: boolean, lap: number,
 *   ma: number,        // 地質年代（百万年前）。未来ステージでは 0。
 *   futureMa: number   // 未来ステージのみ：現在から何百万年後か。それ以外 0。
 * }}
 */
export function eraForEvo(evo) {
  const v = Math.max(0, evo || 0);
  for (let i = 0; i < ERAS.length; i++) {
    const era = ERAS[i];
    if (era.endEvo === Infinity || v < era.endEvo) {
      const intoEra = v - era.startEvo;
      if (era.endEvo === Infinity) {
        // 無限ステージ：FUTURE_LAP ごとに周回する
        const lap = Math.floor(intoEra / FUTURE_LAP) + 1;
        const fraction = (intoEra % FUTURE_LAP) / FUTURE_LAP;
        return {
          era,
          index: i,
          startEvo: era.startEvo,
          endEvo: Infinity,
          intoEra,
          fraction,
          isFuture: true,
          lap,
          ma: 0,
          futureMa: (intoEra / FUTURE_LAP) * FUTURE_LAP_MA,
        };
      }
      const fraction = era.len > 0 ? Math.min(1, intoEra / era.len) : 0;
      return {
        era,
        index: i,
        startEvo: era.startEvo,
        endEvo: era.endEvo,
        intoEra,
        fraction,
        isFuture: false,
        lap: 1,
        ma: era.fromMa + (era.toMa - era.fromMa) * fraction,
        futureMa: 0,
      };
    }
  }
  // 到達不能（ERAS の最後は必ず len:0）。念のためのフォールバック。
  const last = ERAS[ERAS.length - 1];
  return {
    era: last,
    index: ERAS.length - 1,
    startEvo: last.startEvo,
    endEvo: Infinity,
    intoEra: v - last.startEvo,
    fraction: 1,
    isFuture: true,
    lap: 1,
    ma: 0,
    futureMa: 0,
  };
}

/** そのステージ内で、あるチェックポイントが位置する絶対 evo */
function checkpointEvo(era, cp) {
  return era.startEvo + era.len * cp.pos;
}

/**
 * (before, after] の半開区間で新たに到達したチェックポイントを返す。
 * 無限ステージのラップも生成する。
 */
export function checkpointsBetween(before, after) {
  const out = [];
  for (const era of ERAS) {
    if (era.len <= 0) continue;
    for (const cp of era.checkpoints) {
      const at = checkpointEvo(era, cp);
      if (at > before && at <= after) {
        out.push({
          id: `${era.id}:${cp.label}`,
          eraId: era.id,
          em: cp.em,
          label: cp.label,
          note: cp.note,
          at,
        });
      }
    }
  }

  // 未来ステージ：FUTURE_LAP ごとに無限に節目が生まれる
  const future = ERAS[ERAS.length - 1];
  if (future.len <= 0) {
    let k = Math.floor((before - future.startEvo) / FUTURE_LAP);
    if (k < 0) k = 0;
    for (;;) {
      k++;
      const at = future.startEvo + FUTURE_LAP * k;
      if (at <= before) continue;
      if (at > after) break;
      out.push({
        id: `future:lap${k}`,
        eraId: future.id,
        em: "🧬",
        label: `${Math.round(k * FUTURE_LAP_MA * 100)}万年先へ`,
        note: `未来ステージ ${k} 周目`,
        at,
      });
      if (k > 10000) break; // 暴走防止
    }
  }

  out.sort((a, b) => a.at - b.at);
  return out;
}

/** (before, after] で新たに突入した時代 */
export function erasEnteredBetween(before, after) {
  const out = [];
  for (let i = 0; i < ERAS.length; i++) {
    const era = ERAS[i];
    if (era.startEvo > 0 && era.startEvo > before && era.startEvo <= after) {
      out.push({ era, index: i });
    }
  }
  return out;
}

/** 時代の突入とチェックポイントを混ぜた「節目」の一覧（evo 昇順） */
function milestonesAround(evo) {
  const list = [];
  for (const era of ERAS) {
    if (era.startEvo > 0) {
      list.push({
        id: `era:${era.id}`,
        kind: "era",
        em: era.em,
        label: `${era.name} に到達`,
        note: era.desc,
        at: era.startEvo,
      });
    }
    if (era.len > 0) {
      for (const cp of era.checkpoints) {
        list.push({
          id: `${era.id}:${cp.label}`,
          kind: "checkpoint",
          em: cp.em,
          label: cp.label,
          note: cp.note,
          at: checkpointEvo(era, cp),
        });
      }
    }
  }
  // 未来ステージ：現在地の前後1ラップぶんだけ足せば next/last には十分
  const future = ERAS[ERAS.length - 1];
  if (future.len <= 0 && evo >= future.startEvo - FUTURE_LAP) {
    const k0 = Math.max(0, Math.floor((evo - future.startEvo) / FUTURE_LAP));
    for (let k = k0; k <= k0 + 2; k++) {
      if (k < 1) continue;
      list.push({
        id: `future:lap${k}`,
        kind: "checkpoint",
        em: "🧬",
        label: `${Math.round(k * FUTURE_LAP_MA * 100)}万年先へ`,
        note: `未来ステージ ${k} 周目`,
        at: future.startEvo + FUTURE_LAP * k,
      });
    }
  }
  list.sort((a, b) => a.at - b.at);
  return list;
}

/** 直近で通過した節目（なければ null） */
export function lastMilestone(evo) {
  const list = milestonesAround(evo);
  let found = null;
  for (const m of list) {
    if (m.at <= evo) found = m;
    else break;
  }
  return found;
}

/** 次の節目（remaining = そこまでの evo）。無限ステージでも必ず返る。 */
export function nextMilestone(evo) {
  const list = milestonesAround(evo);
  for (const m of list) {
    if (m.at > evo) return { ...m, remaining: m.at - evo };
  }
  return null;
}

/** その時代のチェックポイントを、道の上の道標として使える形で返す */
export function eraWaypoints(era) {
  if (!era || era.len <= 0) return [];
  return era.checkpoints.map((cp) => ({
    pos: cp.pos,
    em: cp.em,
    label: cp.label,
    note: cp.note,
    at: checkpointEvo(era, cp),
  }));
}
